import { motion, useReducedMotion } from 'framer-motion';
import { type ReactNode } from 'react';
import {
  drawLine,
  maskReveal,
  revealGroup,
  revealItem,
  VIEWPORT,
} from '@/lib/motion';

/**
 * Shared shell for every pitch section. Sets the rhythm: full-width with a
 * 6xl content column, generous vertical padding, scroll-anchor id, and a
 * staggered reveal as the user enters it.
 *
 * The header used to move as one rigid block — eyebrow, headline, subtitle
 * and intro all sliding up together on a single 0.7s tween, identically in
 * all ten sections. Now the parts arrive in reading order: the index rule
 * draws out, the headline rises from behind its own baseline, then the
 * supporting copy settles. Same content, but the eye is led through it
 * instead of being handed a finished slab.
 *
 * `density` varies the vertical breathing room so eleven consecutive
 * sections don't beat at exactly the same interval.
 */
export function SectionShell({
  id,
  index,
  eyebrow,
  title,
  subtitle,
  intro,
  children,
  align = 'left',
  density = 'normal',
  /** Optional content parked beside the header, filling the right column. */
  aside,
  /** Optional className for the inner content column. */
  className,
}: {
  id: string;
  /** Investor-deck-style "01 / 10" step indicator. */
  index?: number;
  /** Small all-caps label above the title. */
  eyebrow?: string;
  title: ReactNode;
  /** Hollow tagline rendered immediately under the title. */
  subtitle?: ReactNode;
  /** Short paragraph beneath the title (max 2 lines). */
  intro?: ReactNode;
  children?: ReactNode;
  align?: 'left' | 'center';
  density?: 'tight' | 'normal' | 'open';
  aside?: ReactNode;
  className?: string;
}) {
  const reduced = useReducedMotion() ?? false;

  const alignmentClass =
    align === 'center'
      ? 'items-center text-center [--pitch-intro-max:42rem]'
      : 'items-start text-left [--pitch-intro-max:46rem]';

  const paddingClass = {
    tight: 'py-20 sm:py-24',
    normal: 'py-28 sm:py-36',
    open: 'py-32 sm:py-44',
  }[density];

  const header = (
    <motion.div
      variants={revealGroup(0.09)}
      initial="hidden"
      whileInView="visible"
      viewport={VIEWPORT}
      className={`flex flex-col gap-6 ${alignmentClass}`}
    >
      {(index !== undefined || eyebrow) && (
        <motion.div
          variants={revealItem(10, 0.5)}
          className="flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.22em] text-ink-dim"
        >
          {index !== undefined ? (
            <span className="tabular-nums text-brand-bright">
              {String(index).padStart(2, '0')}
            </span>
          ) : null}
          {index !== undefined && eyebrow ? (
            <motion.span
              aria-hidden
              variants={drawLine}
              style={{ transformOrigin: 'left' }}
              className="h-px w-8 bg-border-strong"
            />
          ) : null}
          {eyebrow ? <span>{eyebrow}</span> : null}
        </motion.div>
      )}

      {/* Bottom padding gives the clip-path room so descenders in the
          headline are never sliced during the mask reveal. */}
      <motion.div variants={maskReveal(reduced)} className="pb-[0.12em]">
        <h2 className="max-w-4xl text-balance text-[40px] font-semibold leading-[1.05] tracking-tight text-ink-strong sm:text-[56px] lg:text-[64px]">
          {title}
        </h2>
      </motion.div>

      {subtitle ? (
        <motion.p
          variants={revealItem(14, 0.6)}
          className="max-w-3xl text-balance text-[20px] font-medium leading-tight text-brand-bright sm:text-[26px]"
        >
          {subtitle}
        </motion.p>
      ) : null}

      {intro ? (
        <motion.p
          variants={revealItem(14, 0.6)}
          className="text-balance text-[17px] leading-relaxed text-ink-muted [max-width:var(--pitch-intro-max)] sm:text-[19px]"
        >
          {intro}
        </motion.p>
      ) : null}
    </motion.div>
  );

  return (
    <section
      id={id}
      className={`relative mx-auto w-full max-w-6xl scroll-mt-24 px-6 sm:px-10 ${paddingClass}`}
    >
      {aside ? (
        <div className="grid items-end gap-10 lg:grid-cols-[minmax(0,1fr)_auto] lg:gap-16">
          {header}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={VIEWPORT}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.28 }}
            className="w-full lg:w-auto"
          >
            {aside}
          </motion.div>
        </div>
      ) : (
        header
      )}

      {children ? (
        <div className={`relative mt-16 ${className ?? ''}`}>{children}</div>
      ) : null}
    </section>
  );
}
